import * as R from "ramda";
import { debounce } from "lodash";
import { useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { writeData } from "../api";
import {
  getImplementation,
  getPageImplementations,
} from "../api/implementation";
import { ActionType, AppDispatch, RootState } from "../reducer";
import {
  AddImplementationsAction,
  AddPageImplementationsAction,
} from "../reducer/implementation";
import {
  Implementation,
  IMPLEMENTATION_LANGUAGE,
} from "../types/implementation";
import { useAppIdMap } from "./app";
import { useFetchProtocol } from "./protocol";
import { ImplementationContext } from "../components/page/block/implementation";

export const useAddImplementations = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (implementations: Implementation[]) => {
    const action: AddImplementationsAction = {
      type: ActionType.ADD_IMPLEMENTATIONS,
      implementations,
    };
    dispatch(action as any);
  };
};

export const useImplementation = (implId: string | undefined) => {
  const implementation = useSelector((state: RootState) => {
    const impls = state.ui.implementations;
    if (!implId) return undefined;
    if (!(implId in impls)) return undefined;
    return impls[implId];
  });
  const addImplementations = useAddImplementations();
  const writeBackend = useMemo(
    () =>
      debounce((impl: Implementation) => {
        return writeData(impl);
      }, 500),
    [implId],
  );
  const setImplementation = (impl: Implementation) => {
    addImplementations([impl]);
    return writeBackend(impl);
  };
  const setImplementationName = async (name: string) => {
    if (!implementation) return;
    const newImplementation: Implementation = { ...implementation, name };
    return setImplementation(newImplementation);
  };
  const setImplementationCode = async (code: string) => {
    if (!implementation) return;
    const newImplementation: Implementation = { ...implementation, code };
    addImplementations([newImplementation]);
    return writeData(newImplementation);
  };
  const setImplementationLanguage = async (
    language: IMPLEMENTATION_LANGUAGE,
  ) => {
    if (!implementation) return;
    const newImplementation: Implementation = { ...implementation, language };
    addImplementations([newImplementation]);
    return writeData(newImplementation);
  };
  const implementationLanguage =
    implementation?.language ?? IMPLEMENTATION_LANGUAGE.PYTHON;
  return {
    implementation,
    setImplementation,
    setImplementationName,
    setImplementationCode,
    setImplementationLanguage,
    implementationLanguage,
  };
};

export const useFetchImplementation = () => {
  const addImplementations = useAddImplementations();
  const { appId } = useAppIdMap();

  return (implId: string) => {
    return getImplementation({
      "implementation-id": implId,
      "app-id": appId!,
    }).then((impl) => {
      addImplementations([impl]);
      return impl;
    });
  };
};

export const useFetchImplementationListener = (implId: string | undefined) => {
  const fetchImplementation = useFetchImplementation();

  useEffect(() => {
    if (!implId) return;
    fetchImplementation(implId);
  }, [implId]);
};

export const usePageImplementationsRoute = (pageId: string | undefined) => {
  const { appId } = useAppIdMap();
  return `/app/${appId}/page/${pageId}/implementations`;
};

export const useFetchPageImplementations = () => {
  const { appId } = useAppIdMap();
  const dispatch = useDispatch<AppDispatch>();
  return (pageId: string) => {
    getPageImplementations({ "app-id": appId!, "page-id": pageId }).then(
      (implementations) => {
        const action: AddPageImplementationsAction = {
          type: ActionType.ADD_PAGE_IMPLEMENTATIONS,
          implementations,
          pageId,
        };
        dispatch(action as any);
      },
    );
  };
};

export const useFetchPageImplementationsListener = (
  pageId: string | undefined,
) => {
  const fetchPageImplementations = useFetchPageImplementations();
  const impls = usePageImplementations(pageId);
  const implsUndefined = impls === undefined;

  useEffect(() => {
    // console.log("FETCHING_IMPLS", pageId, implsUndefined);
    if (!pageId) return;
    // if (!implsUndefined) return;
    fetchPageImplementations(pageId);
  }, [pageId, implsUndefined]);
};

export const usePageImplementations = (pageId: string | undefined) => {
  const pageImplementations = useSelector((state: RootState) => {
    const { implementations, pageImplementations } = state.ui;
    if (!pageId) return undefined;
    if (!(pageId in pageImplementations)) return undefined;
    const pageImpls = R.sortBy(R.identity, pageImplementations[pageId]);
    const impls = pageImpls.map((implId) => implementations[implId]);
    return impls;
  });
  return pageImplementations;
};

export const useImplementationContext = () => {
  const ctxValue = useContext(ImplementationContext);
  if (!ctxValue) throw new Error("Context must be defined");
  return ctxValue;
};
